import { type DeckSetupOptions } from "./decks";
import { isCellarCampaignSeed } from "./seedEvents";
import { beeDiceManager, deckManager, rng, timer } from "..";

// RoundManager.ts
export class RoundManager {
  private round: number;
  private roundElement: HTMLElement;
  private roundElementContainer: HTMLElement;
  private nextRoundButton: HTMLButtonElement;
  private previousRoundButton: HTMLButtonElement;
  private pauseButton: HTMLButtonElement;
  private timerDisplay: HTMLElement;
  private scoringTime: boolean;
  private gameOver: boolean;
  private timerEnabled: boolean;
  private getDeckSetupOptions: () => DeckSetupOptions;
  private onStateChange: (() => void) | null;
  private onGameFinished: (() => void) | null;
  private roundTimes: number[] = [5, 5, 5, 6, 6, 6, 7, 7, 8, 8, 8, 8, 8, 8, 8, 8];
  private scoringMinutes: number = 4;

  constructor() {
    this.scoringTime = false;
    this.gameOver = false;
    this.timerEnabled = false;
    this.getDeckSetupOptions = () => ({});
    this.onStateChange = null;
    this.onGameFinished = null;
    this.round = 1;
    this.roundElement = document.getElementById('round')!;

    this.previousRoundButton = document.getElementById('previousRound') as HTMLButtonElement;
    this.nextRoundButton = document.getElementById('nextRound') as HTMLButtonElement;
    this.roundElementContainer = document.getElementById('round-bg') as HTMLButtonElement;
    this.pauseButton = document.getElementById('pause') as HTMLButtonElement;
    this.timerDisplay = document.querySelector('.timer-display') as HTMLElement;


    this.pauseButton.addEventListener('click', () => {
      if (this.gameOver || !this.timerEnabled) return;

      if (timer.isRunning) {
        timer.pause();
      } else {
        timer.resume();
      }

      this.syncPauseButton();
    });

    this.nextRoundButton.addEventListener('click', () => {
      this.nextRound();
    });

    this.previousRoundButton.addEventListener('click', () => {
      
      let targetRound = this.scoringTime ? this.round : this.round - 1;
      targetRound = Math.max(targetRound, 1);
      this.restartGame();
      this.nextRoundButton.style.display = "";
      

      for (let index = 0; index < targetRound - 1; index++) {
        this.round++;
        deckManager.SetupNextRound();
        this.scoringTime = false;
        this.setTimerForCurrentRound(true);
        console.log('going to next round');
      }

      
      
      deckManager.ShowCurrentCards();
      beeDiceManager.showRound(this.round, false);
      this.updateUI();

      this.syncPauseButton();
    });

    this.updateUI();

    timer.on('finish', () => {
      console.log("The timer has finished!");
      this.nextRound();
      
      // Additional logic when the timer finishes
    });
  }

  public isTimerEnabled(): boolean {
    return this.timerEnabled;
  }

  public setTimerEnabled(isEnabled: boolean): void {
    this.timerEnabled = isEnabled;
    this.setTimerControlsVisible(this.timerEnabled);

    if (!this.timerEnabled) {
      this.disableTimerControls();
      return;
    }

    if (this.gameOver) {
      this.pauseButton.disabled = true;
      this.setPauseButtonState("Game over", false, true);
      return;
    }

    this.setTimerForCurrentRound(false);
  }

  public setDeckSetupOptionsProvider(provider: () => DeckSetupOptions): void {
    this.getDeckSetupOptions = provider;
  }

  public setStateChangeHandler(handler: (() => void) | null): void {
    this.onStateChange = handler;
  }

  public setGameFinishedHandler(handler: (() => void) | null): void {
    this.onGameFinished = handler;
  }

  public getState(): { round: number; scoringTime: boolean; gameOver: boolean; timerEnabled: boolean } {
    return {
      round: this.round,
      scoringTime: this.scoringTime,
      gameOver: this.gameOver,
      timerEnabled: this.timerEnabled
    };
  }

  public restoreProgress(state: { round: number; scoringTime: boolean; gameOver: boolean; timerEnabled?: boolean }): void {
    this.restartGame();

    while (
      this.round !== state.round ||
      this.scoringTime !== state.scoringTime ||
      this.gameOver !== state.gameOver
    ) {
      this.advanceForRestore();

      if (this.gameOver && (
        this.round !== state.round ||
        this.scoringTime !== state.scoringTime ||
        this.gameOver !== state.gameOver
      )) {
        break;
      }
    }

    if (typeof state.timerEnabled === 'boolean') {
      this.timerEnabled = state.timerEnabled;
    }

    timer.stop();
    timer.setDuration(this.getCurrentTimerDuration());
    timer.reset();
    this.nextRoundButton.style.display = this.gameOver ? "none" : "";
    this.pauseButton.disabled = this.gameOver || !this.timerEnabled;
    this.updateUI();
    this.syncPauseButton();
  }

  public restartGame(): void {

    this.gameOver = false;
    this.resetRounds();
    rng.restartGame();

    if (rng.savedGame == true && rng.savedGameFile != null) {
      deckManager.SetupFixedDecks(rng.savedGameFile);
    } else {
      const options = this.getDeckSetupOptions();
      // Match initial setup when replaying a later game of this event campaign.
      if (isCellarCampaignSeed(options.currentSeed) && options.cellarExpansionEnabled) {
        rng.advance(((options.campaignGameNumber ?? 1) - 1) * 100);
      }
      deckManager.SetupDecks(options);
    }

    this.notifyStateChange();

  }

  public resetRounds(): void {
    this.round = 1;
    this.scoringTime = false;
    this.gameOver = false;
    this.nextRoundButton.style.display = "";
    this.setTimerForCurrentRound(false);
    this.updateUI();
    this.notifyStateChange();

  }

  // Move to the next round
  private nextRound(): void {

    if (this.gameOver) return;

    console.log('next round');
    if (this.scoringTime) {
      if (this.round == 12) {
        this.finishGame();
        return;
      }

      this.scoringTime = false;
      this.round++;
      deckManager.SetupNextRound();
      deckManager.ShowCurrentCards();
      beeDiceManager.showRound(this.round, true);
    } else if (this.round == 5 || this.round == 9 || this.round == 12) {
      this.scoringTime = true;
    } else {
      this.round++;
      deckManager.SetupNextRound();
      deckManager.ShowCurrentCards();
      beeDiceManager.showRound(this.round, true);
    }

    this.setTimerForCurrentRound(true);
    this.updateUI();
    this.notifyStateChange();
  }

  private finishGame(): void {
    this.scoringTime = false;
    this.gameOver = true;
    deckManager.HideScoringObjective();
    timer.stop();
    timer.setDuration(0);
    this.nextRoundButton.style.display = "none";
    this.setPauseButtonState("Game over", false, true);
    this.pauseButton.disabled = true;
    this.updateUI();
    this.notifyStateChange();
    this.onGameFinished?.();
  }

  private getRoundDuration(round: number): number {
    return this.roundTimes[round - 1] ?? this.roundTimes[this.roundTimes.length - 1];
  }

  private getCurrentTimerDuration(): number {
    return (this.scoringTime ? this.scoringMinutes : this.getRoundDuration(this.round)) * 60;
  }

  private setTimerForCurrentRound(startTimer: boolean): void {
    timer.setDuration(this.getCurrentTimerDuration());
    timer.reset();

    if (!this.timerEnabled) {
      this.disableTimerControls();
      return;
    }

    this.setTimerControlsVisible(true);
    this.pauseButton.disabled = false;

    if (startTimer) {
      timer.start();
    }

    this.syncPauseButton();
  }

  private disableTimerControls(): void {
    timer.stop();
    this.setTimerControlsVisible(false);
    this.pauseButton.disabled = true;
    this.setPauseButtonState("Start timer", false);
  }

  private setTimerControlsVisible(isVisible: boolean): void {
    this.timerDisplay.classList.toggle('is-hidden', !isVisible);
    this.pauseButton.classList.toggle('is-hidden', !isVisible);
  }

  private syncPauseButton(): void {
    if (!this.timerEnabled) {
      this.disableTimerControls();
      return;
    }

    this.setTimerControlsVisible(true);
    this.pauseButton.disabled = false;
    this.setPauseButtonState(timer.isRunning ? "Pause timer" : "Start timer", timer.isRunning);
  }

  private setPauseButtonState(label: string, isRunning: boolean, isGameOver: boolean = false): void {
    this.pauseButton.classList.toggle('is-running', isRunning);
    this.pauseButton.classList.toggle('is-paused', !isRunning);
    this.pauseButton.classList.toggle('is-game-over', isGameOver);
    this.pauseButton.setAttribute('aria-label', label);
    this.pauseButton.setAttribute('title', label);

    const labelElement = this.pauseButton.querySelector<HTMLElement>('.round-control-label');

    if (labelElement != null) {
      labelElement.textContent = label;
    }
  }


  // Update the UI elements for the round number
  private updateUI(): void {
    console.log('round number = ' + this.round);
    if (this.gameOver) {
      deckManager.HideScoringObjective();
      this.roundElement.innerText = "Game Over!";
      this.roundElementContainer.style.backgroundColor = '#F18A65';
    } else if (this.scoringTime == false) {
      deckManager.HideScoringObjective();
      this.roundElement.innerText = "Round: " + this.round + " / 12";

      if (this.round < 6) {
        this.roundElementContainer.style.backgroundColor = '#ADCD60';
      } else if (this.round < 10) {
        this.roundElementContainer.style.backgroundColor = '#FDC742';
      } else if (this.round < 15) {
        this.roundElementContainer.style.backgroundColor = '#F18A65';
      }

    } else {
      if (this.round < 6) {
        deckManager.ShowScoringObjective(1);
        this.roundElement.innerText = "Season 1 Scoring!";
      } else if (this.round < 10) {
        deckManager.ShowScoringObjective(2);
        this.roundElement.innerText = "Season 2 Scoring!";
      } else if (this.round < 13) {
        deckManager.ShowScoringObjective(3);
        this.roundElement.innerText = "Season 3 Scoring!";
      } else {
        deckManager.HideScoringObjective();
        this.roundElement.innerText = "Game Over!";
      }

    }

  }

  // Get the current round number
  public getCurrentRound(): number {
    return this.round;
  }

  private advanceForRestore(): void {
    if (this.gameOver) return;

    if (this.scoringTime) {
      if (this.round == 12) {
        this.scoringTime = false;
        this.gameOver = true;
        deckManager.HideScoringObjective();
        this.nextRoundButton.style.display = "none";
        return;
      }

      this.scoringTime = false;
      this.round++;
      deckManager.SetupNextRound();
      return;
    }

    if (this.round == 5 || this.round == 9 || this.round == 12) {
      this.scoringTime = true;
      return;
    }

    this.round++;
    deckManager.SetupNextRound();
  }

  private notifyStateChange(): void {
    this.onStateChange?.();
  }


}
